/**
 * Moving what the engraver has already placed.
 *
 * Everything else in this package says what the music *is* and lets the engraver decide
 * where it goes. This is the one layer that overrules it: a note that collides with the
 * line above, a rest crowded by the voice beside it, a passage a publisher wants a little
 * further over. Sibelius and MuseScore both end at the same place -- the arrow keys, moving
 * the selected thing a little way at a time -- and so does this.
 *
 * An offset is kept in tenths, ten to the space between two staff lines, because that is
 * the unit MusicXML measures in and a nudge that survives being exported is worth more than
 * one that looks tidy in the document. It is relative: it says how far from wherever the
 * engraver put the element, never where on the page it sits, so the passage can be reflowed,
 * transposed or dropped into another score and the adjustment still means what it meant.
 *
 * What can be moved is what can be selected -- notes, chords and rests. Marks that hang off
 * the music, like a dynamic or a hairpin, are placed by the engraver alone: a renderer is
 * only asked to keep the ids this package stamps onto notes and bars, and a mark that the
 * drawn page does not name is a mark nothing here can find again to move.
 */

import type { Command } from "../commands/types";
import type { Offset, Score } from "../document/types";
import { focusLocation, selectedLocations } from "./selection";
import type { Selection } from "./selection";

/**
 * How far one press of an arrow moves something, in tenths.
 *
 * Half the space between two staff lines: small enough to place a mark exactly, large
 * enough that one press is visible on a screen at any sensible zoom. Two presses are a
 * whole space, which is the distance most collisions need.
 */
export const NUDGE_STEP = 5;

/**
 * As far as anything may be moved, in tenths: two staff spaces short of a staff's height.
 *
 * A limit is here because an offset is not a placement. Something dragged far enough to
 * leave its own system is not adjusted, it is lost -- there is no scroll that brings it
 * back and no handle on the page to catch it with -- and the way out of a collision that
 * needs more than this is to change the layout, not the note.
 */
export const NUDGE_LIMIT = 60;

function clamp(value: number): number {
  return Math.max(-NUDGE_LIMIT, Math.min(NUDGE_LIMIT, value));
}

function moved(held: Offset | null, x: number, y: number): Offset | null {
  const next = { x: clamp((held?.x ?? 0) + x), y: clamp((held?.y ?? 0) + y) };
  return next.x === 0 && next.y === 0 ? null : next;
}

function same(a: Offset | null, b: Offset | null): boolean {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y;
}

/**
 * Move the selection by a step, in tenths, right and up.
 *
 * Every selected event moves by the same distance from wherever it already sits, rather
 * than to a shared position: a passage nudged clear of the line above keeps its own shape.
 * A selection already as far over as it may go is refused in words, because the alternative
 * -- a key that silently does nothing -- reads as a broken keyboard.
 */
export function nudge(score: Score, selection: Selection, x: number, y: number): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";

  const commands = found.flatMap((at) => {
    const offset = moved(at.event.offset, x, y);
    if (same(offset, at.event.offset)) return [];
    return [{ kind: "setEventProps" as const, eventId: at.event.id, props: { offset } }];
  });
  if (commands.length === 0) return "that is as far as it can be moved by hand";

  return { kind: "batch", label: "nudge", commands };
}

/**
 * Put the selection back where the engraver had it.
 *
 * The way out of an adjustment that was a mistake, and the reason a nudge does not have to
 * be undone stroke by stroke: an offset is a single field and dropping it restores exactly
 * the placing the music would have had if nobody had touched it.
 */
export function reset(score: Score, selection: Selection): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";
  const commands = found
    .filter((at) => at.event.offset !== null)
    .map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { offset: null },
    }));
  if (commands.length === 0) return "nothing here has been moved by hand";
  return { kind: "batch", label: "put back", commands };
}

/**
 * How far the selection has been moved, or `null` where it sits as it was placed.
 *
 * The focus of the selection answers for it, as it does elsewhere: a panel showing one pair
 * of numbers for a passage of notes that were nudged separately would be showing a number
 * that is true of none of them.
 */
export function offsetAt(score: Score, selection: Selection): Offset | null {
  return focusLocation(score, selection)?.event.offset ?? null;
}
